/**
 * ModuleStub — placeholder page for admin modules that haven't been
 * fully ported to this SPA yet.
 *
 * Renders a clear "not ported yet" card with a link to the legacy
 * KGAI admin URL (which still works because the Next.js bundle
 * continues to serve admin.knowledgegate.ai in parallel during the
 * copy-first migration window, per the extraction plan).
 *
 * Use in App.tsx routes for modules that are too heavy to port in
 * this wave but need to exist so the sidebar nav doesn't dead-end.
 */

import { ExternalLink } from "lucide-react";

export interface ModuleStubProps {
  /** Human-facing module name (e.g. "Products", "Question Bank"). */
  name: string;
  /** Short description of what this module does. */
  description?: string;
  /** The path under /admin/... to link back to on KGAI. */
  legacyPath: string;
  /** Estimated line count / complexity note (optional). */
  sizeNote?: string;
}

const LEGACY_ORIGIN = "https://beta.knowledgegate.ai";

export function ModuleStub({
  name,
  description,
  legacyPath,
  sizeNote,
}: ModuleStubProps) {
  const legacyUrl = `${LEGACY_ORIGIN}${legacyPath}`;

  return (
    <div className="max-w-2xl">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-900 dark:bg-amber-900/20">
        <h1 className="text-xl font-semibold text-amber-900 dark:text-amber-200">
          {name} — not yet ported
        </h1>
        {description && (
          <p className="mt-2 text-sm text-amber-800 dark:text-amber-300">
            {description}
          </p>
        )}
        {sizeNote && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            {sizeNote}
          </p>
        )}
        <p className="mt-4 text-sm text-amber-800 dark:text-amber-300">
          This module is part of Phase D of the admin extraction and will
          be ported in a follow-up. For now, use the legacy KGAI version:
        </p>
        <a
          href={legacyUrl}
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600"
        >
          Open on {LEGACY_ORIGIN.replace("https://", "")}
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}
